import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { createPortal } from "react-dom";
import { GripVertical, X } from "lucide-react";
import type {
  ImpactEffortParams,
  ImpactEffortPoint,
} from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { declutterImpactEffortMarkers } from "./declutter.js";
import {
  fanOutPointOffsets,
  impactEffortDisplayYPctToScoreYPct,
  impactEffortPillCenterXPx,
  impactEffortPillSize,
  impactEffortScoreYPctToDisplayYPct,
} from "./fan-out.js";
import {
  DEFAULT_SCALE,
  formatImpactEffortScore,
  tierFor,
  type ImpactEffortScale,
  type Tier,
} from "./model.js";
import { groupImpactEffortOverlaps } from "./overlap-groups.js";

export { fanOutPointOffsets, tierFor };
export type { ImpactEffortScale, Tier };

export interface ImpactEffortPointDragEvent {
  readonly id: string;
  readonly clientX: number;
  readonly clientY: number;
}

export type ImpactEffortOverlapMode = "group" | "declutter" | "none";

/**
 * Impact / Effort Matrix — Verify, singleton. One Effort×Impact scatter plot:
 * a quadrant-tinted stage with each idea positioned by impact/effort, a vertical
 * "Impact" gutter (High→Low) and a horizontal "Effort" strip (High→Low).
 * Ported from the oracle ImpactEffortCardPreview (showcase/solve-2).
 *
 * Mutations route through host-provided callbacks so the body remains
 * persistence-agnostic.
 */
// border-2 + faint pill wash + grip tint per tier (DS tokens; the oracle
// showcase uses these same hues). The label overrides to neutral text-pb-text.
export const TIER_CHIP: Record<Tier, string> = {
  "quick-win": "border-pb-green/60 bg-pb-green/15 text-pb-green-text",
  "major-projects": "border-pb-cyan/60 bg-pb-cyan/15 text-pb-cyan-text",
  "fill-ins": "border-pb-amber/60 bg-pb-amber/15 text-pb-amber-text",
  avoid: "border-pb-red/60 bg-pb-red/15 text-pb-red-text",
};

const TIER_COLOR: Record<Tier, string> = {
  "quick-win": "var(--color-pb-green)",
  "major-projects": "var(--color-pb-cyan)",
  "fill-ins": "var(--color-pb-amber)",
  avoid: "var(--color-pb-red)",
};

/**
 * Category (tier-meaning) vocabulary — the single source shared by the legend
 * and the pill labels so they never disagree (PB-227 review #2). Distinct from
 * the quadrant *position* labels ("Major Projects" / "Fill-ins") the oracle
 * shows on the grid: this is what colour each tier *means*.
 */
export const CATEGORY_LABEL: Record<Tier, string> = {
  "quick-win": "Quick Win",
  "major-projects": "Worth Planning",
  "fill-ins": "Consider",
  avoid: "Avoid",
};

const clamp = (n: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, n));

/** Map a point to plot %: high effort → left, high impact → top. */
export function pointToPct(
  p: { effort: number; impact: number },
  scale: ImpactEffortScale = DEFAULT_SCALE,
): {
  xPct: number;
  yPct: number;
} {
  const span = scale.max - scale.min;
  return {
    xPct:
      ((scale.effortDirection === "ascending"
        ? clamp(p.effort, scale.min, scale.max) - scale.min
        : scale.max - clamp(p.effort, scale.min, scale.max)) /
        span) *
      100,
    yPct: ((scale.max - clamp(p.impact, scale.min, scale.max)) / span) * 100,
  };
}

function pctToScores(
  xPct: number,
  yPct: number,
  scale: ImpactEffortScale,
): {
  effort: number;
  impact: number;
} {
  const span = scale.max - scale.min;
  const snap = (value: number) => Math.round(value / scale.step) * scale.step;
  return {
    effort: clamp(
      snap(
        scale.effortDirection === "ascending"
          ? scale.min + (xPct / 100) * span
          : scale.max - (xPct / 100) * span,
      ),
      scale.min,
      scale.max,
    ),
    impact: clamp(snap(scale.max - (yPct / 100) * span), scale.min, scale.max),
  };
}

export function ImpactEffortWidget({
  data,
  onChange,
  onMove,
  onRemove,
  readOnly,
  compact = true,
  canRemove = true,
  showRemoveWhenDisabled = false,
  groupOverlaps = true,
  overlapMode,
  completeTooltip = false,
  markerLabel,
  removeActionLabel,
  scale = DEFAULT_SCALE,
  quadrantLabels = {},
  categoryLabels = {},
  onPointDragMove,
  onPointDrop,
  onPointDragCancel,
}: WidgetBodyProps<ImpactEffortParams> & {
  onMove?:
    | ((
        id: string,
        patch: Partial<ImpactEffortParams["points"][number]>,
      ) => void)
    | undefined;
  onRemove?: ((id: string) => void) | undefined;
  compact?: boolean | undefined;
  /**
   * Whether a pill may be deleted from the chart. Gated INDEPENDENTLY of
   * `readOnly` so a surface can keep drag while removing the affordance —
   * `readOnly` also disables dragging (`beginDrag` returns early).
   *
   * Defaults `true`, which preserves every existing consumer: note that with no
   * `onRemove` the delete control does NOT no-op, it falls back to `onChange`
   * with the point filtered out, and that fallback IS the live delete for the
   * registry-rendered SOLVE canvas and VSM impact-effort artifact.
   *
   * When `false` the same fixed-width action slot renders as in the `readOnly`
   * branch, so pill width is unchanged and suppressing the button produces no
   * visual delta.
   */
  canRemove?: boolean | undefined;
  /** Keep the fixed-width delete action visible but disabled while the host is
   * preparing removal. Defaults false so existing read-only consumers retain
   * their hidden-control behaviour. */
  showRemoveWhenDisabled?: boolean | undefined;
  /** Collapse touching pills into stable transitive groups. Enabled by default
   * for every host; groups of 2–8 fan open and larger groups open an anchored
   * searchable list. Set false only when a host intentionally needs raw pills. */
  groupOverlaps?: boolean | undefined;
  /** How touching markers are presented. The legacy `groupOverlaps` flag is
   * used when this is omitted, preserving existing SOLVE and VSM behaviour. */
  overlapMode?: ImpactEffortOverlapMode | undefined;
  /** Show the complete point label in the immediate custom tooltip. Opt-in so
   * compact/shared SOLVE and VSM surfaces keep their established presentation. */
  completeTooltip?: boolean | undefined;
  /** Optional visual label for a point on the plot. The complete point label
   * remains the accessible name and tooltip content. */
  markerLabel?: ((point: ImpactEffortPoint) => string) | undefined;
  /** Host-specific removal wording. The shared default remains delete because
   * registry-backed consumers permanently remove the point through onChange. */
  removeActionLabel?: ((point: ImpactEffortPoint) => string) | undefined;
  /** Host-specific score scale. Defaults preserve the shared 0–10 widget. */
  scale?: ImpactEffortScale | undefined;
  /** Host-specific quadrant copy without changing shared defaults. */
  quadrantLabels?: Partial<Record<Tier, string>> | undefined;
  /** Host-specific pill/legend vocabulary without changing shared defaults. */
  categoryLabels?: Partial<Record<Tier, string>> | undefined;
  /** Reports pointer movement outside the plot so an expanded host can expose
   * an additional drop target (for example, a parking tray). */
  onPointDragMove?: ((event: ImpactEffortPointDragEvent) => void) | undefined;
  /** Return true when the host consumed the drop outside the plot. A consumed
   * drop skips the normal coordinate commit. */
  onPointDrop?: ((event: ImpactEffortPointDragEvent) => boolean) | undefined;
  onPointDragCancel?: (() => void) | undefined;
}): ReactElement {
  const plotRef = useRef<HTMLDivElement | null>(null);
  const cleanupDragRef = useRef<(() => void) | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [detachedDragId, setDetachedDragId] = useState<string | null>(null);
  const [livePosition, setLivePosition] = useState<{
    id: string;
    xPct: number;
    yPct: number;
    /** Pointer-up committed this position; hold it until controlled data catches up. */
    committed: boolean;
  } | null>(null);
  const [hovered, setHovered] = useState<{
    point: ImpactEffortPoint;
    rect: DOMRect;
  } | null>(null);
  const [plotSize, setPlotSize] = useState({ width: 600, height: 480 });
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [groupSearch, setGroupSearch] = useState("");
  const [previousGroupPairs, setPreviousGroupPairs] = useState<
    ReadonlySet<string>
  >(new Set());
  const groupSearchInputRef = useRef<HTMLInputElement>(null);
  const groupTriggerRefs = useRef(new Map<string, HTMLButtonElement>());
  const resolvedOverlapMode: ImpactEffortOverlapMode =
    overlapMode ?? (groupOverlaps ? "group" : "none");

  useLayoutEffect(() => {
    const plot = plotRef.current;
    if (!plot) return;
    const measure = (size?: { width: number; height: number }): void => {
      const width = size?.width ?? plot.clientWidth;
      const height = size?.height ?? plot.clientHeight;
      if (width > 0 && height > 0) {
        setPlotSize({ width, height });
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      measure(entry?.contentRect);
    });
    observer.observe(plot);
    return () => observer.disconnect();
  }, []);

  /**
   * What the matrix PLOTS: every point whose id is not in the parking lot.
   *
   * `excludedIds` is the schema's own word for "hidden from this chart without
   * deleting them", and until this the body ignored it — so an idea parked in
   * the expanded editor, or by an approved Park proposal, kept plotting on the
   * card. VSM had already reported the same disagreement from the other side
   * (`VsmImpactEffortArtifact`: "a Kaizen parked there still plotted here") and
   * worked around it by filtering in the expanded editor alone.
   *
   * Rendering only. Every mutation below still reads `data.points`, so a parked
   * point keeps its label and its scores and can be restored to exactly where
   * it was. A caller that pre-filters its points — Hoshin's board sends no
   * `excludedIds` at all — is unaffected.
   */
  const plottedPoints = useMemo(() => {
    const parked = new Set(data.excludedIds ?? []);
    if (parked.size === 0) return data.points;
    return data.points.filter((point) => !parked.has(point.id));
  }, [data.excludedIds, data.points]);

  const overlapLayout = useMemo(
    () =>
      groupImpactEffortOverlaps(
        plottedPoints,
        plotSize,
        resolvedOverlapMode === "group" ? previousGroupPairs : new Set(),
        10,
        (point) => pointToPct(point, scale),
        (point) => markerLabel?.(point) ?? point.label,
      ),
    [
      markerLabel,
      plotSize,
      plottedPoints,
      previousGroupPairs,
      resolvedOverlapMode,
      scale,
    ],
  );
  useEffect(() => {
    const nextPairs =
      resolvedOverlapMode === "group"
        ? overlapLayout.pairKeys
        : new Set<string>();
    if (
      nextPairs.size === previousGroupPairs.size &&
      [...nextPairs].every((pair) => previousGroupPairs.has(pair))
    ) {
      return;
    }
    const frame = requestAnimationFrame(() => setPreviousGroupPairs(nextPairs));
    return () => cancelAnimationFrame(frame);
  }, [overlapLayout.pairKeys, previousGroupPairs, resolvedOverlapMode]);

  const activeExpandedGroupId = overlapLayout.groups.some(
    (group) => group.id === expandedGroupId && group.points.length > 1,
  )
    ? expandedGroupId
    : null;
  const expandedFanOffsets = useMemo(() => {
    const group = overlapLayout.groups.find(
      (candidate) => candidate.id === activeExpandedGroupId,
    );
    if (!group || group.points.length > 8) return new Map();
    const visualPoints = group.points.map((point) => ({
      ...point,
      label: markerLabel?.(point) ?? point.label,
    }));
    return fanOutPointOffsets(visualPoints, scale, {
      group,
      plot: plotSize,
    });
  }, [
    activeExpandedGroupId,
    markerLabel,
    overlapLayout.groups,
    plotSize,
    scale,
  ]);
  const declutterOffsets = useMemo(
    () =>
      resolvedOverlapMode === "declutter"
        ? declutterImpactEffortMarkers(plottedPoints, {
            plot: plotSize,
            positionFor: (point) => pointToPct(point, scale),
            labelFor: (point) => markerLabel?.(point) ?? point.label,
          })
        : new Map(),
    [markerLabel, plotSize, plottedPoints, resolvedOverlapMode, scale],
  );
  const closeExpandedGroup = (): void => {
    const groupId = activeExpandedGroupId;
    setExpandedGroupId(null);
    if (groupId) {
      requestAnimationFrame(() =>
        groupTriggerRefs.current.get(groupId)?.focus(),
      );
    }
  };
  const handleGroupDialogKeyDown = (
    event: KeyboardEvent<HTMLElement>,
  ): void => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    closeExpandedGroup();
  };

  useEffect(() => {
    if (!activeExpandedGroupId) return;
    groupSearchInputRef.current?.focus();
  }, [activeExpandedGroupId]);

  useEffect(() => () => cleanupDragRef.current?.(), []);
  useEffect(() => {
    if (!readOnly) return;
    cleanupDragRef.current?.();
    const frame = requestAnimationFrame(() => setDraggingId(null));
    return (): void => cancelAnimationFrame(frame);
  }, [readOnly]);

  // `onChange` may persist through an API. Keep rendering the dropped position
  // until the controlled value reflects it, so pointer-up never flashes the old
  // server position between local drag state and the optimistic cache update.
  const controlledPoint = livePosition?.committed
    ? data.points.find((candidate) => candidate.id === livePosition.id)
    : undefined;
  const committedScores = livePosition?.committed
    ? pctToScores(livePosition.xPct, livePosition.yPct, scale)
    : undefined;
  const controlledCaughtUp =
    livePosition?.committed === true &&
    (controlledPoint == null ||
      (committedScores != null &&
        Math.abs(controlledPoint.impact - committedScores.impact) < 0.000_001 &&
        Math.abs(controlledPoint.effort - committedScores.effort) < 0.000_001));
  const clearLivePosition = readOnly || controlledCaughtUp;
  useEffect(() => {
    if (!clearLivePosition || !livePosition) return;
    // Clear after paint: controlled data already draws the same coordinates, so
    // this cannot flash; deferring also avoids a cascading render inside effect.
    const frame = requestAnimationFrame(() => {
      setLivePosition((current) => (current === livePosition ? null : current));
      setDetachedDragId((current) =>
        current === livePosition.id ? null : current,
      );
    });
    return (): void => cancelAnimationFrame(frame);
  }, [clearLivePosition, livePosition]);
  const visibleLivePosition = clearLivePosition ? null : livePosition;

  const commitMove = (id: string, xPct: number, yPct: number): void => {
    const scores = pctToScores(xPct, yPct, scale);
    if (onMove) onMove(id, scores);
    else {
      onChange({
        ...data,
        points: data.points.map((point) =>
          point.id === id ? { ...point, ...scores } : point,
        ),
      });
    }
  };

  const beginDrag = (
    event: React.PointerEvent<HTMLButtonElement>,
    id: string,
    display?: {
      readonly xPct: number;
      readonly yPct: number;
      readonly dx?: number;
      readonly dy?: number;
    },
    fromOverflowList = false,
  ): void => {
    if (readOnly || event.button !== 0 || !plotRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    setHovered(null);
    const plot = plotRef.current;
    const rect = plot.getBoundingClientRect();
    const localWidth = Math.max(
      1,
      plot.clientWidth || rect.width || plotSize.width,
    );
    const localHeight = Math.max(
      1,
      plot.clientHeight || rect.height || plotSize.height,
    );
    const borderBoxWidth = Math.max(1, plot.offsetWidth || localWidth);
    const borderBoxHeight = Math.max(1, plot.offsetHeight || localHeight);
    const scaleX = rect.width > 0 ? rect.width / borderBoxWidth : 1;
    const scaleY = rect.height > 0 ? rect.height / borderBoxHeight : 1;
    const screenLeft = rect.left + plot.clientLeft * scaleX;
    const screenTop = rect.top + plot.clientTop * scaleY;
    const point = data.points.find((candidate) => candidate.id === id);
    if (!point) return;
    const start = { x: event.clientX, y: event.clientY };
    const activePointerId = event.pointerId;
    let moved = false;
    // A second drag can begin before the first API write settles. Anchor it to
    // the position the user still sees, not the older controlled value.
    const pointPosition = fromOverflowList
      ? pointToPct(point, scale)
      : visibleLivePosition?.id === id
        ? visibleLivePosition
        : display
          ? display
          : pointToPct(point, scale);
    const displayXOffset = "dx" in pointPosition ? (pointPosition.dx ?? 0) : 0;
    const displayYOffset = "dy" in pointPosition ? (pointPosition.dy ?? 0) : 0;
    const pill = impactEffortPillSize(markerLabel?.(point) ?? point.label);
    const displayedYPct = impactEffortScoreYPctToDisplayYPct(
      pointPosition.yPct,
      localHeight,
      pill.height,
    );
    const displayedPoint = {
      x:
        screenLeft +
        (impactEffortPillCenterXPx(pointPosition.xPct, localWidth, pill.width) +
          displayXOffset) *
          scaleX,
      y:
        screenTop +
        ((displayedYPct / 100) * localHeight + displayYOffset) * scaleY,
    };
    const grabOffset = fromOverflowList
      ? { x: 0, y: 0 }
      : {
          x: event.clientX - displayedPoint.x,
          y: event.clientY - displayedPoint.y,
        };
    setDraggingId(id);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const positionFor = (clientX: number, clientY: number) => ({
      xPct: clamp(
        ((clientX - grabOffset.x - screenLeft) / (localWidth * scaleX)) * 100,
        0,
        100,
      ),
      yPct: impactEffortDisplayYPctToScoreYPct(
        ((clientY - grabOffset.y - screenTop) / (localHeight * scaleY)) * 100,
        localHeight,
        pill.height,
      ),
    });
    const move = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== activePointerId) return;
      if (
        Math.abs(pointerEvent.clientX - start.x) +
          Math.abs(pointerEvent.clientY - start.y) >
        4
      ) {
        moved = true;
      }
      if (!moved) return;
      pointerEvent.preventDefault();
      if (fromOverflowList) {
        setExpandedGroupId(null);
        setDetachedDragId(id);
      }
      const next = positionFor(pointerEvent.clientX, pointerEvent.clientY);
      setLivePosition({ id, ...next, committed: false });
      onPointDragMove?.({
        id,
        clientX: pointerEvent.clientX,
        clientY: pointerEvent.clientY,
      });
    };
    const finish = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== activePointerId) return;
      cleanup();
      setDraggingId(null);
      if (!moved) return;
      const consumed =
        onPointDrop?.({
          id,
          clientX: pointerEvent.clientX,
          clientY: pointerEvent.clientY,
        }) === true;
      if (consumed) {
        setLivePosition(null);
        setDetachedDragId(null);
        return;
      }
      const next = positionFor(pointerEvent.clientX, pointerEvent.clientY);
      setLivePosition({ id, ...next, committed: true });
      commitMove(id, next.xPct, next.yPct);
    };
    const cancel = (pointerEvent: PointerEvent): void => {
      if (pointerEvent.pointerId !== activePointerId) return;
      cleanup();
      setDraggingId(null);
      setLivePosition(null);
      if (fromOverflowList) setDetachedDragId(null);
      onPointDragCancel?.();
    };
    const cleanup = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", cancel);
      cleanupDragRef.current = null;
    };
    cleanupDragRef.current?.();
    cleanupDragRef.current = cleanup;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", cancel);
  };

  const moveWithKeyboard = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    point: ImpactEffortPoint,
  ): boolean => {
    if (readOnly) return false;
    const keyboardStep = Math.max(1, scale.step);
    const effortStep =
      scale.effortDirection === "ascending" ? -keyboardStep : keyboardStep;
    const patch: Partial<ImpactEffortPoint> = {};
    if (event.key === "ArrowUp")
      patch.impact = clamp(point.impact + keyboardStep, scale.min, scale.max);
    else if (event.key === "ArrowDown")
      patch.impact = clamp(point.impact - keyboardStep, scale.min, scale.max);
    else if (event.key === "ArrowLeft")
      patch.effort = clamp(point.effort + effortStep, scale.min, scale.max);
    else if (event.key === "ArrowRight")
      patch.effort = clamp(point.effort - effortStep, scale.min, scale.max);
    else return false;

    event.preventDefault();
    if (
      (patch.impact === undefined || patch.impact === point.impact) &&
      (patch.effort === undefined || patch.effort === point.effort)
    )
      return false;
    if (onMove) onMove(point.id, patch);
    else {
      onChange({
        ...data,
        points: data.points.map((candidate) =>
          candidate.id === point.id ? { ...candidate, ...patch } : candidate,
        ),
      });
    }
    return true;
  };

  const prepareRemove = (
    event: React.PointerEvent<HTMLButtonElement>,
    pointId: string,
  ): void => {
    // The chip's sibling is a pointer-capturing drag handle. End any stale drag
    // before this gesture can be interpreted by the plot/canvas, and suppress
    // the compatible mouse gesture while keeping the semantic click for mouse,
    // touch, and keyboard activation.
    event.preventDefault();
    event.stopPropagation();
    cleanupDragRef.current?.();
    setDraggingId(null);
    setDetachedDragId((current) => (current === pointId ? null : current));
    setLivePosition((current) => (current?.id === pointId ? null : current));
    setHovered(null);
  };

  const removePoint = (point: ImpactEffortPoint): void => {
    if (onRemove) {
      onRemove(point.id);
      return;
    }
    const next: ImpactEffortParams = {
      ...data,
      points: data.points.filter((candidate) => candidate.id !== point.id),
    };
    if (data.excludedIds !== undefined) {
      next.excludedIds = data.excludedIds.filter((id) => id !== point.id);
    }
    onChange(next);
  };

  const renderPoint = (
    point: ImpactEffortPoint,
    display?: {
      readonly xPct: number;
      readonly yPct: number;
      readonly dx?: number;
      readonly dy?: number;
    },
  ): ReactElement => {
    const live =
      visibleLivePosition?.id === point.id ? visibleLivePosition : null;
    const position = live ?? display ?? pointToPct(point, scale);
    const dragging = draggingId === point.id;
    const visibleLabel = markerLabel?.(point) ?? point.label;
    const pill = impactEffortPillSize(visibleLabel);
    const yPct = impactEffortScoreYPctToDisplayYPct(
      position.yPct,
      plotSize.height,
      pill.height,
    );
    const liveScores = live ? pctToScores(live.xPct, live.yPct, scale) : null;
    const tier = tierFor(
      liveScores?.impact ?? point.impact,
      liveScores?.effort ?? point.effort,
      scale,
    );
    const categoryLabel = categoryLabels[tier] ?? CATEGORY_LABEL[tier];
    const tx =
      position.xPct === 0 ? "0%" : position.xPct === 100 ? "-100%" : "-50%";
    const dx = "dx" in position ? (position.dx ?? 0) : 0;
    const dy = "dy" in position ? (position.dy ?? 0) : 0;
    const transform =
      dx === 0 && dy === 0
        ? `translate(${tx}, -50%)`
        : `translate(${tx}, -50%) translate(${dx}px, ${dy}px)`;
    const transitionClass =
      resolvedOverlapMode === "declutter" && !dragging
        ? "transition-[left,top,transform,box-shadow] duration-motion-state ease-motion-standard"
        : "transition-shadow";
    return (
      <div
        key={point.id}
        data-testid="impact-effort-pill"
        data-dragging={dragging || undefined}
        className={`group/chip absolute inline-flex h-6 items-center rounded-full border-2 bg-pb-card motion-reduce:transition-none ${transitionClass} ${dragging ? "z-40 shadow-xl ring-2 ring-pb-teal/50" : "z-10 shadow-sm"} ${TIER_CHIP[tier]}`}
        style={{ left: `${position.xPct}%`, top: `${yPct}%`, transform }}
        title={
          compact
            ? `Impact ${formatImpactEffortScore(point.impact, scale)} · Effort ${formatImpactEffortScore(point.effort, scale)}`
            : undefined
        }
      >
        {/* eslint-disable-next-line ds/no-hand-built -- the plotted idea's drag handle: pointer capture and arrow-key moves on the matrix, which no design-system Button carries */}
        <button
          type="button"
          disabled={readOnly}
          onPointerDown={(event) => beginDrag(event, point.id, display)}
          onKeyDown={(event) => moveWithKeyboard(event, point)}
          onPointerEnter={(event) =>
            !compact &&
            !live &&
            setHovered({
              point,
              rect: event.currentTarget.getBoundingClientRect(),
            })
          }
          onPointerLeave={() => setHovered(null)}
          onFocus={(event) =>
            !compact &&
            setHovered({
              point,
              rect: event.currentTarget.getBoundingClientRect(),
            })
          }
          onBlur={() => setHovered(null)}
          className={`inline-flex touch-none items-center gap-1 disabled:cursor-default ${dragging ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"}`}
          aria-label={`Move ${point.label}: ${categoryLabel}, impact ${formatImpactEffortScore(point.impact, scale)} of ${scale.max}, effort ${formatImpactEffortScore(point.effort, scale)} of ${scale.max}`}
        >
          <span className="inline-flex items-center justify-center ps-1">
            <GripVertical className="h-2.5 w-2.5" aria-hidden />
          </span>
          <span className="max-w-[8.75rem] truncate py-0.5 text-dense-1 font-medium text-pb-text">
            {visibleLabel}
          </span>
        </button>
        {(!readOnly && canRemove) || showRemoveWhenDisabled ? (
          // eslint-disable-next-line ds/no-hand-built -- the 16px remove mark inside a 24px plotted chip on the matrix canvas; a design-system Button is 24px at its smallest
          <button
            type="button"
            disabled={readOnly || !canRemove}
            aria-label={
              removeActionLabel?.(point) ?? `Delete idea ${point.label}`
            }
            onPointerDown={(event) => prepareRemove(event, point.id)}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              removePoint(point);
            }}
            className="me-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-pb-text-muted transition-colors hover:bg-pb-red/10 hover:text-pb-red-text focus-ring disabled:cursor-wait disabled:hover:text-pb-text-muted motion-reduce:transition-none"
          >
            <X className="h-3 w-3" />
          </button>
        ) : (
          <span aria-hidden className="me-1 inline-flex h-4 w-4 shrink-0" />
        )}
      </div>
    );
  };

  const visibleGroups =
    resolvedOverlapMode === "group"
      ? overlapLayout.groups
      : plottedPoints.map((point) => ({
          id: point.id,
          points: [point],
          ...pointToPct(point, scale),
        }));
  return (
    <div className="flex-1 min-h-0 flex">
      <div className="shrink-0 flex flex-col items-center justify-between text-dense-1 uppercase tracking-wider font-semibold text-pb-text-muted py-1 pe-1.5">
        <span>High</span>
        <span
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            fontWeight: 700,
          }}
        >
          Impact
        </span>
        <span>Low</span>
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <div
          ref={plotRef}
          data-testid="impact-effort-plot"
          className={`relative flex-1 min-h-0 rounded-md border border-pb-border bg-pb-card-elevated/40 overflow-hidden select-none ${draggingId ? "cursor-grabbing" : ""}`}
        >
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pointer-events-none">
            <div
              style={
                scale.effortDirection === "ascending" ? { order: 2 } : undefined
              }
              className={`bg-pb-cyan/[0.08] border-b border-pb-border-strong/50 ${scale.effortDirection === "ascending" ? "" : "border-e"}`}
            />
            <div
              style={
                scale.effortDirection === "ascending" ? { order: 1 } : undefined
              }
              className={`bg-pb-green/[0.1] border-b border-pb-border-strong/50 ${scale.effortDirection === "ascending" ? "border-e" : ""}`}
            />
            <div
              style={
                scale.effortDirection === "ascending" ? { order: 4 } : undefined
              }
              className={`bg-pb-red/[0.08] border-pb-border-strong/50 ${scale.effortDirection === "ascending" ? "" : "border-e"}`}
            />
            <div
              style={
                scale.effortDirection === "ascending" ? { order: 3 } : undefined
              }
              className={`bg-pb-amber/[0.08] border-pb-border-strong/50 ${scale.effortDirection === "ascending" ? "border-e" : ""}`}
            />
          </div>

          <QuadLabel
            className={
              scale.effortDirection === "ascending"
                ? "top-1.5 end-2 text-end"
                : "top-1.5 start-2"
            }
            label={quadrantLabels["major-projects"] ?? "Major Projects"}
            tone="text-pb-cyan-text/80"
          />
          <QuadLabel
            className={
              scale.effortDirection === "ascending"
                ? "top-1.5 start-2"
                : "top-1.5 end-2 text-end"
            }
            label={quadrantLabels["quick-win"] ?? "Quick Wins"}
            tone="text-pb-green-text/90"
          />
          <QuadLabel
            className={
              scale.effortDirection === "ascending"
                ? "bottom-1.5 end-2 text-end"
                : "bottom-1.5 start-2"
            }
            label={quadrantLabels.avoid ?? "Avoid"}
            tone="text-pb-red-text"
          />
          <QuadLabel
            className={
              scale.effortDirection === "ascending"
                ? "bottom-1.5 start-2"
                : "bottom-1.5 end-2 text-end"
            }
            label={quadrantLabels["fill-ins"] ?? "Fill-ins"}
            tone="text-pb-amber-text/90"
          />

          {visibleGroups.map((group) => {
            if (group.points.length === 1) {
              const point = group.points[0];
              if (!point) return null;
              const offset = declutterOffsets.get(point.id);
              return renderPoint(
                point,
                offset
                  ? {
                      ...pointToPct(point, scale),
                      ...offset,
                    }
                  : undefined,
              );
            }
            const expanded = activeExpandedGroupId === group.id;
            const tiers = [
              ...new Set(
                group.points.map((point) =>
                  tierFor(point.impact, point.effort, scale),
                ),
              ),
            ];
            const colors = tiers.map((tier) => TIER_COLOR[tier]);
            const badgeBackground =
              colors.length === 1
                ? colors[0]
                : `conic-gradient(${colors.map((color, index) => `${color} ${(index / colors.length) * 100}% ${((index + 1) / colors.length) * 100}%`).join(", ")})`;
            const filteredPoints = group.points.filter((point) =>
              point.label
                .toLocaleLowerCase()
                .includes(groupSearch.trim().toLocaleLowerCase()),
            );
            return (
              <div key={group.id}>
                {/* eslint-disable-next-line ds/no-hand-built -- the overlap marker plotted on the matrix: a count disc painted with its ideas' tier colours at its matrix position, not a control a design-system Button draws */}
                <button
                  ref={(element) => {
                    if (element)
                      groupTriggerRefs.current.set(group.id, element);
                    else groupTriggerRefs.current.delete(group.id);
                  }}
                  type="button"
                  data-testid="impact-effort-group"
                  aria-expanded={expanded}
                  aria-label={`${group.points.length} overlapping Kaizens`}
                  onClick={() => {
                    if (expanded) closeExpandedGroup();
                    else setExpandedGroupId(group.id);
                    setGroupSearch("");
                  }}
                  className="absolute z-30 flex h-8 min-w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-pb-text/85 px-2 text-xs font-bold text-pb-card shadow-lg hover:shadow-xl press focus-ring motion-reduce:transition-none"
                  style={{
                    left: `${group.xPct}%`,
                    top: `${impactEffortScoreYPctToDisplayYPct(
                      group.yPct,
                      plotSize.height,
                      32,
                    )}%`,
                    background: badgeBackground,
                  }}
                >
                  {group.points.length}
                </button>
                {expanded && group.points.length <= 8
                  ? group.points.map((point) => {
                      const offset = expandedFanOffsets.get(point.id) ?? {
                        dx: 0,
                        dy: -64,
                      };
                      return renderPoint(point, {
                        xPct: group.xPct,
                        yPct: group.yPct,
                        ...offset,
                      });
                    })
                  : null}
                {expanded && group.points.length >= 9 ? (
                  <dialog
                    open
                    aria-label={`${group.points.length} overlapping Kaizens`}
                    className="absolute z-40 flex max-h-64 w-72 -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-pb-border bg-pb-card shadow-xl"
                    style={{
                      left: `${group.xPct}%`,
                      top: `${Math.min(
                        80,
                        impactEffortScoreYPctToDisplayYPct(
                          group.yPct,
                          plotSize.height,
                          32,
                        ) + 5,
                      )}%`,
                    }}
                  >
                    <label className="border-b border-pb-border p-2 text-xs text-pb-text-muted">
                      Search this group
                      <input
                        ref={groupSearchInputRef}
                        value={groupSearch}
                        onKeyDown={handleGroupDialogKeyDown}
                        onChange={(event) => setGroupSearch(event.target.value)}
                        className="mt-1 w-full rounded border border-pb-control-border bg-pb-card px-2 py-1 text-sm text-pb-text"
                      />
                    </label>
                    <div className="overflow-auto">
                      {filteredPoints.map((point) => {
                        const tier = tierFor(point.impact, point.effort, scale);
                        return (
                          // eslint-disable-next-line ds/no-hand-built -- a drag handle out of the overlap group onto the matrix: pointer capture and arrow-key moves, which no design-system Button carries
                          <button
                            key={point.id}
                            type="button"
                            onKeyDown={(event) => {
                              handleGroupDialogKeyDown(event);
                              if (moveWithKeyboard(event, point)) {
                                closeExpandedGroup();
                              }
                            }}
                            onPointerDown={(event) =>
                              beginDrag(event, point.id, undefined, true)
                            }
                            onClick={closeExpandedGroup}
                            aria-label={`Move ${point.label} from grouped list: ${categoryLabels[tier] ?? CATEGORY_LABEL[tier]}, impact ${formatImpactEffortScore(point.impact, scale)} of ${scale.max}, effort ${formatImpactEffortScore(point.effort, scale)} of ${scale.max}`}
                            className={`flex w-full touch-none items-center gap-2 border-b border-pb-border px-3 py-2 text-start text-xs hover:pb-overlay-paint ${readOnly ? "cursor-default" : "cursor-grab active:cursor-grabbing"}`}
                          >
                            <GripVertical
                              className="h-3.5 w-3.5 shrink-0 text-pb-text-muted"
                              aria-hidden
                            />
                            <span className="truncate font-medium">
                              {point.label}
                            </span>
                            <span className="shrink-0 text-pb-text-muted">
                              {categoryLabels[tier] ?? CATEGORY_LABEL[tier]} · I{" "}
                              {formatImpactEffortScore(point.impact, scale)} · E{" "}
                              {formatImpactEffortScore(point.effort, scale)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </dialog>
                ) : null}
              </div>
            );
          })}
          {detachedDragId
            ? (() => {
                const point = data.points.find(
                  (candidate) => candidate.id === detachedDragId,
                );
                return point ? renderPoint(point) : null;
              })()
            : null}
          {/* Kept hand-built (design-system adoption): the live drag readout
              is a chart mark on the matrix, placed under the pointer's drag,
              not a floating control bar. */}
          {visibleLivePosition && !visibleLivePosition.committed ? (
            <div className="pointer-events-none absolute bottom-2 start-1/2 z-20 -translate-x-1/2 rounded-md border glass px-2 py-1 text-dense-1 font-medium text-pb-text shadow-lg">
              Impact {Math.round(100 - visibleLivePosition.yPct)}% · Effort{" "}
              {Math.round(100 - visibleLivePosition.xPct)}%
            </div>
          ) : null}
        </div>
        <div className="shrink-0 flex items-center justify-between text-dense-1 uppercase tracking-wider font-semibold text-pb-text-muted px-1 pt-1.5">
          <span>{scale.effortDirection === "ascending" ? "Low" : "High"}</span>
          <span style={{ fontWeight: 700 }}>Effort</span>
          <span>{scale.effortDirection === "ascending" ? "High" : "Low"}</span>
        </div>
      </div>
      {hovered
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-tooltip min-w-35 -translate-x-1/2 -translate-y-full rounded-md border border-pb-border bg-pb-card-elevated px-2.5 py-2 text-dense-1 text-pb-text shadow-md"
              style={{
                left: hovered.rect.left + hovered.rect.width / 2,
                top: hovered.rect.top - 8,
              }}
            >
              <div
                className={
                  completeTooltip
                    ? "max-w-80 whitespace-normal break-words text-xs font-semibold"
                    : "max-w-45 truncate text-xs font-semibold"
                }
              >
                {hovered.point.label}
              </div>
              <div className="font-medium">
                {(() => {
                  const tier = tierFor(
                    hovered.point.impact,
                    hovered.point.effort,
                    scale,
                  );
                  return categoryLabels[tier] ?? CATEGORY_LABEL[tier];
                })()}
              </div>
              <div className="mt-1 flex gap-3">
                {scale.displayDecimals !== undefined ? (
                  <>
                    <span>
                      Impact{" "}
                      {formatImpactEffortScore(hovered.point.impact, scale)}
                    </span>
                    <span>
                      Effort{" "}
                      {formatImpactEffortScore(hovered.point.effort, scale)}
                    </span>
                  </>
                ) : (
                  <>
                    <span>
                      Impact{" "}
                      {Math.round(
                        ((hovered.point.impact - scale.min) /
                          (scale.max - scale.min)) *
                          100,
                      )}
                      %
                    </span>
                    <span>
                      Effort{" "}
                      {Math.round(
                        ((hovered.point.effort - scale.min) /
                          (scale.max - scale.min)) *
                          100,
                      )}
                      %
                    </span>
                  </>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function QuadLabel({
  className,
  label,
  tone,
}: {
  className: string;
  label: string;
  tone: string;
}): ReactElement {
  return (
    <div
      className={`pointer-events-none absolute ${className} ${tone}`}
      aria-hidden
    >
      <div className="font-bold uppercase tracking-wider text-dense-2">
        {label}
      </div>
    </div>
  );
}
